/**
 * Web env adapter for the storage-URL domain (`lib/storage/url.ts`).
 *
 * Binds the pure resolver to `NEXT_PUBLIC_SUPABASE_URL`. That variable is
 * inlined into the client bundle by Next, so the same function works in server
 * components, route handlers, client components and email templates.
 *
 * Import THIS from delivery code (components, hooks, routes, email senders);
 * import `./url` only when you need the pure `base`-parameterised form (tests,
 * the mobile parity pin).
 *
 * @see lib/storage/url.ts  behaviour table + contract decisions
 * @see .tondocs/images/HANDOFF_RELATIVE_STORAGE_PATHS_2026_07_27.md
 */

import {
  buildPublicUrl,
  extractStoragePath,
  type ImageRefInput,
  resolveStorageUrl as resolveStorageUrlWithBase,
} from "@/lib/storage/url";

export { buildPublicUrl, extractStoragePath };
export { STORAGE_BUCKET, isPassthroughRef, objectUrlFromRenderUrl } from "@/lib/storage/url";
export type { ImageRefInput, RawImageRef } from "@/lib/storage/url";
export {
  asRawImageRef,
  asRawImageRefs,
  rawImageRefValue,
  rawImageRefValues,
} from "@/lib/storage/url";

/**
 * Supabase project URL, trailing slash trimmed. Empty only in a misconfigured
 * environment — `resolveStorageUrl` then throws for relative paths (and only
 * for those), which is the fail-fast the handoff §3 L2 asks for.
 */
export function storageBaseUrl(): string {
  return (process.env.NEXT_PUBLIC_SUPABASE_URL ?? "").replace(/\/+$/, "");
}

/**
 * Bucket-relative path → absolute public URL; everything else (absolute URL,
 * `blob:`, `data:`, `/local-asset.svg`, empty) passes through unchanged.
 * Idempotent, so it is safe to call in a funnel whose input may already have
 * been resolved upstream.
 */
export function resolveStorageUrl(raw: ImageRefInput): string;
export function resolveStorageUrl(
  raw: ImageRefInput | null | undefined,
): string | null | undefined;
export function resolveStorageUrl(
  raw: ImageRefInput | null | undefined,
): string | null | undefined {
  return resolveStorageUrlWithBase(raw, storageBaseUrl());
}

/** `resolveStorageUrl` over an array, skipping nullish/empty entries. */
export function resolveStorageUrls(
  raws: ReadonlyArray<ImageRefInput | null | undefined>,
): string[] {
  const base = storageBaseUrl();
  const out: string[] = [];
  for (const raw of raws) {
    if (!raw) continue;
    out.push(resolveStorageUrlWithBase(raw, base));
  }
  return out;
}
